// Thin DTOs decoupled from the legacy @shared/models classes (which carry
// constructor side-effects, 'ZZ' sort sentinels, and translation keys in
// status fields). Only fields the wizard actually consumes are kept.

import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { DynamicPriceRuleShape } from '@shared/functions/resolve-dynamic-price-state';
import { PayableOption } from '@shared/models/payable-option';

// Title of the synthetic "Anzahlung" position. Shared so the commit (position
// title) and the Rechnungsversand row label stay in sync — selectBelegPositions
// matches an ad-hoc deposit row to its saved position by title.
export const RESERVATION_DEPOSIT_TITLE_KEY = 'bookingV2.reservation.deposit.positionTitle';

export interface BookingV2BerthOption {
    id: number;
    handle: string;
    pierHandle: string | null;
    // Pier (Steg) id — passed to dynamic-price evaluation when a rule references the
    // pier (the backend doesn't derive it from the berth). `null` if unknown.
    pierId: number | null;
    length: number;
    width: number;
    maxDraft: number;
}

// One berth occupancy (Liegeplatzbelegung) captured by the wizard — a berth held
// for its OWN time window (6609). A booking can carry several: at year start a
// broad reservation, then guests slotted onto different berths for different
// sub-periods. Mirrors the legacy `BerthBoatAssignment` (berth + from/to), which
// the backend already persists per assignment. `berth === null` is the berth-less
// case (a boat held with no berth); at most one occupancy per berth (one-per-berth,
// keyed by berth id in the pickers).
export interface BookingV2BerthOccupancy {
    // Stable client key for @for tracking and card identity (not persisted).
    uuid: string;
    // The chosen berth; null while a freshly added card has no berth yet, and for
    // the berth-less assignment.
    berth: BookingV2BerthOption | null;
    // Occupancy window (Belegungszeitfenster). `openEnd` → `to` is null.
    from: Date | null;
    to: Date | null;
    openEnd: boolean;
    // UI state (not persisted): while `false`, the window mirrors the booking
    // date-step range and re-syncs when it changes; the user pins an override by
    // editing the window in the expanded berth row. Loaded assignments come back
    // as overridden (they carry an explicit saved window).
    overridden?: boolean;
    // Edit mode: the existing berth-assignment row id (undefined = newly added).
    assignmentId?: number;
}

// Winter storage (Lagerplatz) picked for dynamic-price evaluation — rules can
// branch on it (width/length/name). Thin option: the picker needs the handle to
// display, the id to send. Not persisted on the booking (pure pricing input).
export interface BookingV2WinterStorageOption {
    id: number;
    handle: string;
}

export type BookingV2BoatOrigin = 'existing' | 'manual' | 'new';

export interface BookingV2BoatOption {
    id: number;
    name: string | null;
    licensePlate: string | null;
    // Always the LüA (length over all) — the harbour calculates with it, so
    // display, fit filter and dynamic price all read this field.
    length: number;
    width: number;
    // Mirrors the legacy field name `depth` (Berth uses `maxDraft` — kept
    // separate to avoid mapping confusion).
    depth: number;
    // Area in m², resolved by `resolveBoatArea()`. Carried explicitly because it
    // cannot be derived here: `length` above is the LüA, while the stored area and
    // the SQL price engine calculate with the hull length. `null` if unknown.
    sqm: number | null;
    ownerLabel: string | null;
    // Owner reference for a new boat: the existing person/company picked as
    // owner, to be linked when the booking is committed. `null` = no owner.
    ownerId?: number | null;
    ownerKind?: 'person' | 'company' | null;
    // 'existing' = real DB record (id > 0).
    // 'manual'   = user-typed dimensions only, never persisted.
    // 'new'      = full draft to be created when the booking is committed.
    // Defaults to 'existing' on records returned by the search.
    origin?: BookingV2BoatOrigin;
}

// LüA, width and depth only — what the berth fit and the dimension-only search work with.
export type BookingV2BoatDimensions = Pick<BookingV2BoatOption, 'length' | 'width' | 'depth'>;

// Unified owner view: Person and Company can both own a boat and share
// the same UI section. `kind` only drives icon and label.
export interface BookingV2OwnerOption {
    id: number;
    kind: 'person' | 'company';
    fullName: string;
    boats: BookingV2BoatOption[];
}

export type BookingV2PersonKind = 'person' | 'company';

export type BookingV2PersonOrigin = 'existing' | 'new';

// The booking's main person/organization. Persons and companies share one
// UI section; `kind` only drives icon and which fields are shown.
export interface BookingV2PersonOption {
    id: number;
    kind: BookingV2PersonKind;
    // Person `fullName` or company `name`.
    displayName: string;
    // Company contact person (`null` for persons).
    contactPerson: string | null;
    address: string | null;
    phone: string | null;
    mail: string | null;
    // 'existing' = real DB record (id > 0).
    // 'new'      = quick-create draft (negative id), persisted at booking commit.
    origin: BookingV2PersonOrigin;
    // Raw quick-create fields, present only for origin === 'new'. The combined
    // displayName above is lossy (a person's first/last name), so the booking
    // commit builds the nested-create payload from these.
    draft?: BookingV2NewPersonDraft;
}

export interface BookingV2NewPersonDraft {
    firstName: string | null;   // person
    lastName: string | null;    // person
    salutation: number | null;  // person only (Salutation enum value); null for company
    companyName: string | null; // company
    contactPerson: string | null;
    address: string | null;
    phone: string | null;
    mail: string | null;
}

// "Beziehung" (a tenant relation type — `TenantRelation` in the legacy
// model). Only fields the wizard renders are kept.
export interface BookingV2RelationOption {
    id: number;
    name: string;
    note: string | null;
    hexColor: string | null;
    hexBgColor: string | null;
    // Edit mode: existing tenant-relation-assignment row id (undefined = newly picked).
    assignmentId?: number;
    // The relation's billing interval (Jährlich/Monatlich/…) — drives proration
    // (calculateProportionalTime) and the open-ended vesting period. Carried so the
    // commit mapper can bill the relation's Leistungen like the legacy dialog does.
    payableOption?: PayableOption | null;
    // The Leistungen attached to this Beziehung (read-only preview + billing
    // source). Fixed-end booking → prorated one-off positions; open-ended → billed
    // by the subscription scheduler from the relation row.
    products?: readonly BookingV2RelationProduct[];
    // Distinct entity ids the dynamic-price rules of the attached products reference
    // (boat / berth / season / pier / winterStorage / …), aggregated via
    // `priceRuleMap.getRequirements()`. Drives which pickers/hints the wizard shows.
    requiredEntities?: readonly string[];
}

// One product attached to a Beziehung (via the tenant-relation↔product
// pivot). Quantity rides on the pivot; price/unit/VAT come from the product.
export interface BookingV2RelationProduct {
    productId: number;
    name: string;
    quantity: number;
    unitId: number | null;
    unitLabelKey: string | null;
    // The unit's stable key ('overnight_stay', 'day', 'piece', …). Decides whether the
    // quantity follows the stay duration instead of the configured one. (#7017)
    unitUniqueName: string | null;
    // Tax-rate row id — needed to bill the product as a one-off position
    // (the preview only needs `vatValue`, the position insert needs the id).
    taxRateId: number | null;
    // `price` is the catalog base initially; for a rule-based product it is
    // OVERWRITTEN with the backend-evaluated `rulePrice` once the booking context
    // (boat / berth / relation / season) is known — see BookingV2RelationPricingService.
    price: number;
    vatValue: number;
    // True when the product carries dynamic-price rule sets → its price must be
    // evaluated against the booking context (and a season may need picking).
    hasDynamicPrice: boolean;
    // The matched rule's name after evaluation — the line's Bezeichnung (#7044);
    // null until resolved.
    dynamicRuleName?: string | null;
    // The full backend-evaluated dynamic price, sent back on the billed position so
    // the backend persists the rule provenance (bcm_dynamic_prices). Null until
    // resolved / when the product is not rule-based.
    dynamicPrice?: Partial<BcmDynamicPriceDto> | null;
    // How many selecting conditions the product's rules carry, per engine. Together
    // with `dynamicPrice` this tells a genuine rule miss (bill nothing) from a
    // formula-only product (bill the computed price). (#7017)
    priceRuleShape?: DynamicPriceRuleShape | null;
    // Beziehungen the product's rules name (`getTenantRelationCriteriaIds()`). When the
    // booking's own relation is not among them, the rule needs another one picked. (#7044)
    tenantRelationCriteriaIds?: readonly number[];
}

// Hand-edited Bezeichnung / Einzelpreis of one relation Leistung; only the fields
// that differ from the rule are set. Keyed by productId. (#7044)
export interface BookingV2RelationLineOverride {
    title?: string;
    price?: number;
}

export type BookingV2RelationLineOverrides = Readonly<Record<number, BookingV2RelationLineOverride>>;

// productId → Beziehung the product's price rule is evaluated with, for products
// whose rules name a relation other than the booking's own. (#7044)
export type BookingV2PricingRelationIds = Readonly<Record<number, number>>;

// ─── Advanced booking (travelers / power / keys / services / documents) ──────
// Entities the wizard's advanced step holds in the facade. Catalog-style
// reference data (power cabinets, key catalog, product catalog, the read-only
// subscription/receipt history) is mock data local to each tab component until
// the backend endpoints are wired (see REDESIGN_BUCHUNGSDIALOG.md §8/B).

export interface BookingV2Traveler {
    firstName: string;
    lastName: string;
}

// ── Electric meter (Strom) — real backend entities ──
// Source models: ElectricMeterCabinet → ElectricMeter (Zähler), billing kind =
// ElectricMeterProductType. Loaded via ElectricMeterCabinetApiService /
// ElectricMeterApiService / ElectricMeterProductTypeApiService. Mirrors the
// legacy AddElectricMeterDialog flow (cabinet → meter → from → start reading →
// billing type).

export interface BookingV2ElectricMeterCabinetOption {
    id: number;
    handle: string;
    pierHandle: string | null;
    // Pre-composed display label (`handle · pier`) for the select.
    label: string;
    meterCount: number;
    // Provider switching (mirrors `cabinet.provider.switchableViaWeb`):
    //   null  → no provider (manual meter: start reading typed by hand).
    //   true  → web-switchable (CampConcept/ZFA): API-controlled, reading read live.
    //   false → physical-only provider: not bookable here (`disabled`).
    switchableViaWeb: boolean | null;
    // Physical-only providers are shown but not selectable, like the legacy dialog.
    disabled: boolean;
}

export interface BookingV2ElectricMeterOption {
    id: number;
    handle: string;
    // `${cabinet.handle} ${handle}` — what the meter is shown as.
    fullName: string;
    cabinetId: number | null;
    fuse: string | null;
    // Highest known reading — default + lower bound for the start reading.
    lastReading: number;
    // Meter already carries boat assignments (shown as a hint).
    occupied: boolean;
    // Meter has an unfinished reading (no end value) — can't be attached until it
    // is closed; the backend would otherwise reject the booking save.
    hasOpenReading: boolean;
    // Combined "not selectable" flag for the dropdown (occupied or open reading).
    disabled: boolean;
    // DualUse meter (#7250) — display-only, shown with the `dual_use` module.
    hasDualUse: boolean;
}

export interface BookingV2ElectricMeterProductTypeOption {
    id: number;
    // ElectricMeterProductKey (BY_USAGE | PREPAID | FLATRATE | NO_CHARGE).
    key: string;
    // Settings field holding this type's default product (resolveDefaultProductId).
    settingsKey: string;
    // Backend-provided label (already localized server-side).
    description: string;
}

// Electricity product (Stromgebühr category) chosen for billing.
export interface BookingV2ElectricityProductOption {
    id: number;
    name: string;
    price: number;
    // `name · 12,50 €` — display label for the select.
    label: string;
    // Carried so an additional-fee line can be built as a proper invoice position.
    taxRateId: number | null;
    unitId: number | null;
    // VAT% of the product's tax rate — drives the gross shown in the
    // Rechnungsversand summary for a flat-rate (Festbetrag) connection.
    vatValue: number;
}

// Optional flat "base fee" (Grundgebühr) charged alongside a by-usage meter —
// emitted as a standalone invoice position linked to the meter assignment.
export interface BookingV2ElectricMeterAdditionalFee {
    productId: number;
    title: string;
    price: number;
    quantity: number;
    unitId: number | null;
    taxRateId: number | null;
}

// One electric-meter assignment captured by the wizard. Carries the fields the
// booking workflow needs to build an ElectricMeterAssignment on save.
export interface BookingV2ElectricMeterAssignment {
    uuid: string;
    // Edit mode: existing assignment + reading id, needed to remove on save
    // (undefined = newly added; untouched existing meters are not re-sent).
    assignmentId?: number;
    readingId?: number;
    cabinetId: number;
    cabinetHandle: string;
    meterId: number;
    meterHandle: string;
    meterFullName: string;
    from: Date | null;
    meterReadingStart: number;
    // Closing reading + date once disconnected (Trennen). `meterReadingEnd` is
    // also set after a Zwischenablesung (interim reading), for display.
    meterReadingEnd: number | null;
    to: Date | null;
    // Which berth occupancy (Liegeplatzbelegung) the connection attaches to.
    // Defaults to the primary berth; only matters when the booking spans several.
    berthId: number | null;
    berthLabel: string | null;
    productTypeId: number;
    productTypeKey: string;
    productTypeLabel: string;
    // Billing product (Stromgebühr) + amount. `null` for NO_CHARGE; quantity is
    // `null` for BY_USAGE (consumption-based) and NO_CHARGE.
    productId: number | null;
    productName: string | null;
    quantity: number | null;
    // Unit net price + VAT% of the billing product — kept only so the
    // Rechnungsversand summary can show the Strom gross (the backend itself bills
    // from productType + product). `null` when there is no billing product.
    productPrice: number | null;
    productVatValue: number | null;
    // Optional flat base fee for by-usage meters (Grundgebühr); `null` otherwise.
    additionalFee: BookingV2ElectricMeterAdditionalFee | null;
    // Provider switching (mirrors `cabinet.provider`):
    //   null  → manual meter: readings typed by hand, all actions available.
    //   true  → web-switchable (CampConcept/ZFA): read live, all actions available.
    //   false → physical-only provider: interim/disconnect actions disabled.
    provider: { switchableViaWeb: boolean } | null;
    // DualUse meter (#7250) — display-only, shown with the `dual_use` module.
    hasDualUse?: boolean;
    // Edit mode: an existing assignment marked for disconnect on commit — sent
    // through the backend disconnect path (close-out + power-off) rather than a
    // hard delete.
    disconnected?: boolean;
    // Create mode: a boat's pre-existing OPEN standalone assignment, proposed for
    // take-over ("übernehmen"). On commit it is RE-PARENTED onto the booking's
    // berth assignment (PATCH only) — never created or disconnected. Kept separate
    // from `assignmentId` because edit-mode baseline meters also carry an id.
    reparent?: boolean;
}

export type BookingV2KeyType = 'physical' | 'qr' | 'numeric' | 'rfid';

export const BOOKING_V2_KEY_TYPE_LABEL_KEYS: Record<BookingV2KeyType, string> = {
    physical: 'bookingV2.advanced.keys.types.physical',
    qr: 'bookingV2.advanced.keys.types.qr',
    numeric: 'bookingV2.advanced.keys.types.numeric',
    rfid: 'bookingV2.advanced.keys.types.rfid',
};

export type BookingV2KeyUniqueness = 'unique' | 'duplicated';

// Where the lent key came from: an existing catalog key, or a key drafted
// inline in the "new" form (persisted at booking commit).
export type BookingV2KeyOrigin = 'catalog' | 'new';

// One key lending captured by the wizard. Mirrors the legacy `KeyAssignment`:
// a key handed to a person and/or a boat for a period, with an optional
// deposit (Pfand). The booking save will turn each of these into a
// KeyAssignment via PersonKeyApiService.
export interface BookingV2KeyAssignment {
    uuid: string;
    origin: BookingV2KeyOrigin;
    // Edit mode: existing lending row id (undefined = newly lent).
    assignmentId?: number;

    // ── The lent key ──
    // Catalog → backend `Key.id`. New → null (draft, created at booking commit).
    catalogKeyId: number | null;
    keyName: string;
    // Catalog → raw `Key.keyType` text. New → null (the enum `keyType` is used).
    keyTypeLabel: string | null;
    // New → fixed enum type. Catalog → null.
    keyType: BookingV2KeyType | null;
    keyIdentifier: string | null;
    uniqueness: BookingV2KeyUniqueness;

    // ── Borrower (at least one of person / boat is set) ──
    personId: number | null;
    personKind: BookingV2PersonKind | null;
    personLabel: string | null;
    boatId: number | null;
    boatLabel: string | null;

    // ── Lending window ──
    keyNumber: string | null;
    from: Date | null;          // issued on (Ausgabe am)
    to: Date | null;            // returned on (Zurück am)
    note: string | null;

    // ── Deposit (Pfand) ──
    // Catalog → from the key's linked deposit product. New → manual amount.
    // `null` deposit amount = no deposit.
    depositProductId: number | null;
    depositProductName: string | null;
    depositAmount: number | null;
    // Tax rate + unit of the deposit product, so the Pfand can be billed as a real
    // invoice position (the position insert requires both ids); vatValue drives the
    // gross shown in the Rechnungsversand summary. `null` when there is no deposit.
    depositTaxRateId: number | null;
    depositUnitId: number | null;
    depositVatValue: number | null;
    keyFeePayedOn: Date | null;        // deposit paid on
    keyFeePayedBackOn: Date | null;    // deposit refunded on
}

// A backend key (catalog entry). Mapped from the `Key` model via
// KeyApiService.getAll(). `keyType` is a free-text field server-side, so it is
// kept as a raw label (`typeLabel`) rather than the fixed BookingV2KeyType set.
export interface BookingV2KeyCatalogEntry {
    id: string;
    name: string;
    // Raw `Key.keyType` text — shown as-is (no enum mapping).
    typeLabel: string;
    identifier?: string;
    uniqueness: BookingV2KeyUniqueness;
    // No reliable backend source for stock count — left undefined for now.
    totalCopies?: number;
    // Linked deposit product (Schlüsselpfand). undefined = no deposit.
    depositProductId?: number;
    depositProductName?: string;
    depositAmount?: number;
    // Tax rate / unit / VAT% of the deposit product — needed to bill the Pfand.
    depositTaxRateId?: number;
    depositUnitId?: number;
    depositVatValue?: number;
}

// Subscription configuration for a service line, mirroring the core fields of
// the legacy GetProductSubscriptionDialog. The billing interval is a
// user-picked `PayableOption`; `fromDate`/`toDate` are the subscription window
// (first/last position) while `vestingPeriodFrom`/`vestingPeriodUntil` are the
// first service period (Leistungszeitraum) that regenerates per interval.
export interface BookingV2SubscriptionConfig {
    payableOptionId: number | null;
    payableOptionLabelKey: string | null;
    fromDate: Date | null;
    toDate: Date | null;
    vestingPeriodFrom: Date | null;
    vestingPeriodUntil: Date | null;
    lastDayOfMonth: boolean;
}

// One service line captured by the wizard. Carries real backend references
// (product / unit / tax rate / payable option) so the booking save can build
// an InvoicePosition (or ProductSubscription) without re-resolving anything.
export interface BookingV2Service {
    id: string;
    // Edit mode: existing row id — position (one-off) or subscription (undefined = new).
    positionId?: number;
    subscriptionId?: number;
    // Edit mode: already settled on an invoice/receipt → read-only, never sent.
    isProtected?: boolean;
    // Catalog product → `Product.id`. Ad-hoc free-text line → null.
    productId: number | null;
    name: string;
    // Product category name, display only (`null` for ad-hoc lines).
    category: string | null;
    quantity: number;
    // `IUnit.id` + its translation key (shown via the translate pipe).
    unitId: number | null;
    unitLabelKey: string | null;
    price: number;
    // `ITaxRate.id` + its numeric value (kept for the gross calculation).
    taxRateId: number | null;
    vatValue: number;
    // True when the catalog product carries dynamic-price rule sets → its price is
    // evaluated against the booking context by BookingV2ServicePricingService (and a
    // season may need picking). Ad-hoc lines (productId null) are never dynamic.
    hasDynamicPrice?: boolean;
    // The matched rule's name after evaluation; null until resolved.
    dynamicRuleName?: string | null;
    // The name the wizard last wrote itself (catalog name, then the matched rule
    // name). While `name` still equals it the line follows the rule; a typed name
    // differs and is kept. `null` = the name is the user's own. (#7044)
    autoName?: string | null;
    // The full backend-evaluated dynamic price, sent back on the billed position/
    // subscription so the backend persists the rule provenance (bcm_dynamic_prices);
    // does NOT change `price`. Null until resolved / non-rule-based.
    dynamicPrice?: Partial<BcmDynamicPriceDto> | null;
    // Distinct entity ids the product's rules reference, captured at add-time from
    // priceRuleMap.getRequirements() — drives the combined season picker/gating even
    // after the services tab (and its catalog) is unmounted.
    requiredEntities?: readonly string[];
    // Beziehungen the product's rules name, captured at add-time like requiredEntities. (#7044)
    tenantRelationCriteriaIds?: readonly number[];
    // Beziehung picked for the price rule when the booking's own relation is not
    // among the criteria. (#7044)
    pricingRelationId?: number | null;
    discount?: number;
    costCenterId?: number | null;
    serviceStart: Date | null;
    serviceEnd: Date | null;
    serviceEndOpen?: boolean;
    isSubscription?: boolean;
    subscriptionConfig?: BookingV2SubscriptionConfig;
    // Set when the line was seeded from a berth's Produkte (#6734) instead of being
    // added by hand. Drives the "Liegeplatz" badge, the reconcile (a line follows
    // its berth in and out of the booking) and the billed position's berth link.
    berthOrigin?: BookingV2BerthProductOrigin;
}

export interface BookingV2BerthProductOrigin {
    // Berth whose assignment the billed position is linked to.
    berthId: number;
    // False for a line recovered on EDIT from an already saved berth position: it is
    // an ordinary editable service now and the reconcile must leave it alone — the
    // berth is remembered only so the re-save keeps its bcm_berth_has_assignments_id
    // (with several berth assignments the backend cannot infer it).
    managed: boolean;
    // Berth-product flag: a zero-total line is dropped instead of billed at 0,00 €.
    ignoreFreeOfChargePosition: boolean;
    // Signature (quantity + Leistungszeitraum) of what the reconcile last wrote.
    // While the line still matches it, a changed occupancy window re-derives both;
    // as soon as the user edits either, the line is theirs and is left alone.
    // Empty for a line recovered on edit (`managed: false`), which is never synced.
    seededSignature: string;
}

// Read-only subscription history for the selected existing person/company.
// Mapped from the backend `ProductSubscription` (loaded via persons/:id or
// companies/:id, which eager-load `subscriptions` with product + payableOption).
export interface BookingV2Subscription {
    id: string;
    // customTitle || dynamicPrice.ruleName || product.name
    description: string;
    quantity: number;
    // product.unit.translationKey (rendered via the translate pipe).
    unitLabelKey: string | null;
    // customPrice ?? dynamicPrice.rulePrice ?? product.price
    unitPrice: number;
    // unitPrice * quantity
    total: number;
    // fromDate
    startDate: Date | null;
    // payableOption.translationKey (the billing interval label).
    intervalLabelKey: string | null;
    // !toDate || toDate >= today
    isActive: boolean;
}

// Booking-v2 collapses the richer FinancialRecordStatus set into the badge
// kinds the read-only view needs. `overdue` is derived (open + dueDate past),
// not a backend status.
export type BookingV2ReceiptStatusKind = 'paid' | 'partial' | 'open' | 'overdue' | 'cancelled';

// Read-only receipt/invoice history for the selected existing person/company.
// Mapped from the backend `BcmFinancialRecord` (financial-records endpoint).
export interface BookingV2Receipt {
    id: string;
    // record.type — already a valid translate key (cf. legacy `type | translate`).
    typeLabelKey: string;
    number: string;
    date: Date | null;
    dueDate: Date | null;
    status: BookingV2ReceiptStatusKind;
    // record.total (gross).
    grossAmount: number;
    // record.paymentTypeKey — already a translate key.
    paymentMethodKey: string | null;
}

// A document attached to the booking. The file is uploaded to `bcm_files` as
// soon as it is picked (POST /files) and referenced here by `bcmFilesId`; the
// commit then creates a `bcm_documents` row linking it to the booking.
export interface BookingV2BookingDocument {
    id: string;          // client-side tracking id (list keying)
    // Edit mode: existing bcm_documents row id — the only key removals are sent with (`{ id, deleted }`).
    documentId?: number;
    name: string;        // user-entered title → bcm_documents.title
    categoryId: number | null;   // → bcm_documents.bcm_document_categories_id (backend falls back to "Sonstiges")
    categoryName: string | null; // display only
    bcmFilesId: number;  // id returned from POST /files
    fileName: string;    // real file name (File.name)
    mimeType?: string;   // File.type
    size?: number;       // File.size (bytes)
    uploadDate: Date;
}
